import PropTypes from 'prop-types';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { approveStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/approve';
import { cancelStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/cancel';
import { fetchStockReceiptDetail } from '#/api/imsMgmt/receipts/stockReceipt/fetchDetail';
import { rejectStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/reject';
import { submitStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/submit';
import SideMsg from '#/modules/commonComponents/SideMsg';
import DocumentSideMsgActions from '#/modules/imsMgmt/receipts/shared/DocumentSideMsgActions';
import {
  canApproveStockReceipt,
  canCancelStockReceipt,
  canRejectStockReceipt,
  canSubmitStockReceipt,
} from '#/modules/imsMgmt/receipts/shared/receiptStatus';
import { useDocumentSideMsgWorkflow } from '#/modules/imsMgmt/receipts/shared/useDocumentSideMsgWorkflow.jsx';
import {
  stockReceiptSideMsgDetailBlock,
  stockReceiptSideMsgStatusBlock,
} from '#/modules/imsMgmt/receipts/stockReceipt/data/stockReceiptSideMsgConfig';
import {
  getStockReceiptApiErrorMessage,
  isStockReceiptVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/apiError';
import {
  stockReceiptDetailQueryKey,
  stockReceiptListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CONFIRM_COPY = {
  submit: {
    title: '提交入庫單',
    message: '提交後將進入待核准狀態，確定提交？',
    confirmText: '提交',
  },
  approve: {
    title: '核准入庫單',
    message: '核准後將建立庫存批次，確定核准？',
    confirmText: '核准',
  },
  reject: {
    title: '退回入庫單',
    message: '退回後將恢復為草稿，確定退回？',
    confirmText: '退回',
  },
  cancel: {
    title: '刪除草稿',
    message: '刪除後無法復原，確定刪除？',
    confirmText: '刪除',
  },
};

const APIS = {
  submit: submitStockReceipt,
  approve: approveStockReceipt,
  reject: rejectStockReceipt,
  cancel: cancelStockReceipt,
  fetchDetail: fetchStockReceiptDetail,
};

const StockReceiptSideMsg = ({ displayData, setSelectedData }) => {
  const canCreate = useCan(PERM.STOCK_RECEIPT_CREATE);
  const canReview = useCan(PERM.STOCK_RECEIPT_REVIEW);

  const workflow = useDocumentSideMsgWorkflow({
    row: displayData,
    setSelectedData,
    confirmCopy: CONFIRM_COPY,
    canSubmit: (status) => canCreate && canSubmitStockReceipt(status),
    canApprove: (status) => canReview && canApproveStockReceipt(status),
    canReject: (status) => canReview && canRejectStockReceipt(status),
    canCancel: (status) => canCreate && canCancelStockReceipt(status),
    apis: APIS,
    listQueryKeyPrefix: stockReceiptListQueryKeyPrefix,
    detailQueryKey: stockReceiptDetailQueryKey,
    getErrorMessage: getStockReceiptApiErrorMessage,
    isVersionConflict: isStockReceiptVersionConflict,
  });

  const hasActions = (
    workflow.canSubmit
    || workflow.canApprove
    || workflow.canReject
    || workflow.canCancel
  );

  const blocks = [
    stockReceiptSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <DocumentSideMsgActions
                canSubmit={workflow.canSubmit}
                canApprove={workflow.canApprove}
                canReject={workflow.canReject}
                canCancel={workflow.canCancel}
                isPending={workflow.isPending}
                onSubmit={workflow.handleSubmit}
                onApprove={workflow.handleApprove}
                onReject={workflow.handleReject}
                onCancel={workflow.handleCancel}
              />
            </div>
          ),
        },
      ],
    },
    stockReceiptSideMsgDetailBlock,
  ];

  return (
    <>
      {workflow.ConfirmDialogHost}
      <workflow.RejectDialogHost />
      <SideMsg
        title="入庫單"
        titleValue="receiptNo"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

StockReceiptSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default StockReceiptSideMsg;
